
import React, { useState, useEffect, useRef } from 'react';
import { toolsData } from '../data/toolsData';
import { clinicalGuidesData } from '../data/clinicalGuidesData';
import type { DrugInfo } from '../types';

// Icons
const SearchIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>;
const XIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>;
const DrugIcon = () => <span className="text-lg">💊</span>;
const ToolIcon = () => <span className="text-lg">🛠️</span>;
const GuideIcon = () => <span className="text-lg">📖</span>;
const ChevronRight = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>;
const TagIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-yellow-400 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>;

interface SmartSearchProps {
    uid: string | null;
    onSelectDrug: (drugId: string) => void;
    onSelectTool: (toolName: string) => void;
    onSelectGuide: (topic: string, source: string) => void;
    placeholder?: string;
}

interface SearchResults {
    drugs: DrugInfo[];
    tools: { name: string; category: string; matchedTag?: string }[];
    guides: { topic: string; source: string; category: string }[];
}

const SmartSearch: React.FC<SmartSearchProps> = ({ uid, onSelectDrug, onSelectTool, onSelectGuide, placeholder }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<SearchResults>({ drugs: [], tools: [], guides: [] });
    const [isOpen, setIsOpen] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    // Flatten tools for searching with tags
    const flatTools = Object.entries(toolsData).flatMap(([cat, tools]) => 
        tools.map(t => ({ name: t.name, category: cat, tags: t.tags }))
    );

    // Flatten guides for searching
    const flatGuides = clinicalGuidesData.flatMap(cat => 
        cat.topics.map(t => ({ topic: t.name, source: t.source, category: cat.name }))
    );

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const performSearch = async () => {
            if (!query.trim()) {
                setResults({ drugs: [], tools: [], guides: [] });
                setIsOpen(false);
                return;
            }

            setIsSearching(true);
            const lowerQ = query.toLowerCase();

            // 1. Search Tools (Name OR Tag)
            const matchedTools = flatTools
                .map(t => {
                    const nameMatch = t.name.toLowerCase().includes(lowerQ);
                    // Find the specific tag that matched, if any
                    const tagMatch = t.tags.find(tag => tag.toLowerCase().includes(lowerQ));
                    
                    if (nameMatch || tagMatch) {
                        return {
                            name: t.name,
                            category: t.category,
                            matchedTag: !nameMatch && tagMatch ? tagMatch : undefined // Only show tag if name didn't match directly
                        };
                    }
                    return null;
                })
                .filter(Boolean) // Remove nulls
                .slice(0, 4); // Limit to top 4

            // 2. Search Guides (Sync)
            const matchedGuides = flatGuides
                .filter(g => g.topic.toLowerCase().includes(lowerQ) || g.category.toLowerCase().includes(lowerQ))
                .slice(0, 3);

            // 3. Search Drugs (Async - mimics network/DB latency slightly)
            let matchedDrugs: DrugInfo[] = [];
            if (uid) {
                try {
                    // Dynamic import to avoid circular dependency issues
                    const { getAllDrugs } = await import('../services/pharmacyService');
                    const allDrugs = await getAllDrugs(uid);
                    matchedDrugs = allDrugs.filter(d => 
                        d.brandName.toLowerCase().includes(lowerQ) || 
                        d.genericName.toLowerCase().includes(lowerQ)
                    ).slice(0, 4);
                } catch (e) {
                    console.error(e);
                }
            }

            setResults({
                drugs: matchedDrugs,
                tools: matchedTools as any,
                guides: matchedGuides
            });
            setIsSearching(false);
            setIsOpen(true);
        };

        const timeoutId = setTimeout(performSearch, 300); // Debounce
        return () => clearTimeout(timeoutId);
    }, [query, uid]);

    const hasResults = results.drugs.length > 0 || results.tools.length > 0 || results.guides.length > 0;

    const handleItemClick = (action: () => void) => {
        action();
        setIsOpen(false);
        setQuery('');
    };

    return (
        <div ref={wrapperRef} className="relative w-full max-w-3xl mx-auto z-50">
            <div className={`relative group transition-all duration-300 ${isFocused ? 'transform scale-[1.01]' : 'scale-100'}`}>
                {/* Ambient Glow Effect */}
                <div className={`absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-yellow-400 rounded-2xl blur opacity-20 transition duration-500 ${isFocused ? 'opacity-50' : 'opacity-0'}`}></div>
                
                <div className="relative flex items-center">
                    <div className={`absolute left-4 pointer-events-none transition-colors duration-300 ${isFocused ? 'text-yellow-400' : 'text-gray-500'}`}>
                        <SearchIcon />
                    </div>
                    <input
                        type="text"
                        className="block w-full pl-12 pr-12 py-4 bg-[#131c2e] border border-blue-800/30 rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:bg-[#1a2a47] focus:border-transparent transition-all duration-200 shadow-xl text-lg font-medium relative z-10"
                        placeholder={placeholder || "Search drugs, tools, or guidelines..."}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onFocus={() => { setIsFocused(true); if (query) setIsOpen(true); }}
                        onBlur={() => setIsFocused(false)}
                    />
                    {query && (
                        <button 
                            onClick={() => { setQuery(''); setIsOpen(false); }}
                            className="absolute right-4 p-1 rounded-full hover:bg-white/10 text-gray-400 hover:text-white transition-colors z-20"
                        >
                            <XIcon />
                        </button>
                    )}
                </div>
            </div>

            {isOpen && (query.length > 0) && (
                <div className="absolute mt-3 w-full bg-[#1a2a47]/95 backdrop-blur-xl border border-blue-900/80 rounded-2xl shadow-2xl overflow-hidden max-h-[60vh] overflow-y-auto custom-scrollbar ring-1 ring-black/20 z-50 animate-fade-in-down">
                    {!hasResults && !isSearching ? (
                        <div className="p-8 text-center text-gray-400">
                            <p className="text-sm">No results found for "{query}"</p>
                        </div>
                    ) : (
                        <div className="py-2">
                            {/* Tools Section */}
                            {results.tools.length > 0 && (
                                <div className="mb-2">
                                    <h3 className="px-4 py-2 text-xs font-bold text-blue-300 uppercase tracking-wider bg-white/5 sticky top-0 backdrop-blur-md">Clinical Tools</h3>
                                    {results.tools.map((tool, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => handleItemClick(() => onSelectTool(tool.name))}
                                            className="w-full px-4 py-3 text-left hover:bg-blue-600/20 flex items-center justify-between group transition-colors border-b border-white/5 last:border-0"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-blue-900/30 rounded-lg"><ToolIcon /></div>
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <p className="font-semibold text-white group-hover:text-yellow-400 transition-colors">{tool.name}</p>
                                                        {tool.matchedTag && (
                                                            <span className="flex items-center text-[10px] bg-yellow-400/10 text-yellow-300 px-2 py-0.5 rounded-full border border-yellow-400/20">
                                                                <TagIcon /> Matches "{tool.matchedTag}"
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-gray-400">{tool.category}</p>
                                                </div>
                                            </div>
                                            <ChevronRight />
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Drugs Section */}
                            {results.drugs.length > 0 && (
                                <div className="mb-2">
                                    <h3 className="px-4 py-2 text-xs font-bold text-green-300 uppercase tracking-wider bg-white/5 sticky top-0 backdrop-blur-md">Drug Index</h3>
                                    {results.drugs.map((drug, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => handleItemClick(() => onSelectDrug(drug.id))}
                                            className="w-full px-4 py-3 text-left hover:bg-green-600/20 flex items-center justify-between group transition-colors border-b border-white/5 last:border-0"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-green-900/30 rounded-lg"><DrugIcon /></div>
                                                <div>
                                                    <p className="font-semibold text-white group-hover:text-yellow-400 transition-colors">{drug.brandName}</p>
                                                    <p className="text-xs text-gray-400">{drug.genericName}</p>
                                                </div>
                                            </div>
                                            <ChevronRight />
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Guides Section */}
                            {results.guides.length > 0 && (
                                <div>
                                    <h3 className="px-4 py-2 text-xs font-bold text-yellow-300 uppercase tracking-wider bg-white/5 sticky top-0 backdrop-blur-md">Clinical Guidelines</h3>
                                    {results.guides.map((guide, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => handleItemClick(() => onSelectGuide(guide.topic, guide.source))}
                                            className="w-full px-4 py-3 text-left hover:bg-yellow-600/20 flex items-center justify-between group transition-colors border-b border-white/5 last:border-0"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-yellow-900/30 rounded-lg"><GuideIcon /></div>
                                                <div>
                                                    <p className="font-semibold text-white group-hover:text-yellow-400 transition-colors">{guide.topic}</p>
                                                    <p className="text-xs text-gray-400">{guide.source} • {guide.category}</p>
                                                </div>
                                            </div>
                                            <ChevronRight />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SmartSearch;
